import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "../lib/api";
import {
  format, subDays, startOfWeek, endOfWeek,
  startOfMonth, endOfMonth, subWeeks, subMonths,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  TrendingUp, Calendar, ArrowUpDown, ArrowUp, ArrowDown,
  Search, Info, AlertTriangle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import DashboardLayout from "../components/DashboardLayout";
import { toast } from "sonner";
import { API } from "../App";
import { getActiveOrgId } from "../lib/api";

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtEur = (v) => v == null ? "—" : `€${(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`;
const fmtPct = (v) => v == null ? "—" : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2 })}%`;
const fmtX   = (v) => v == null ? "—" : `${Number(v).toLocaleString("es-ES", { minimumFractionDigits: 2 })}x`;
const fmtInt = (v) => (v == null ? "—" : (v || 0).toLocaleString("es-ES"));

// ─── Decision logic ───────────────────────────────────────────────────────────
const DEFAULT_THRESHOLDS = { umbral_escalar: 20, umbral_mantener: 10, umbral_halo_proteger: 1.3 };

function calcDecision(row, thresholds = DEFAULT_THRESHOLDS) {
  const spend = row.meta_spend || 0;
  if (!spend || spend <= 0) return { key: "sin_publicidad", label: "Sin publicidad", color: "gray" };
  const rent = row.rent_real_pct;
  const halo = row.halo_score;
  const { umbral_escalar, umbral_mantener, umbral_halo_proteger } = thresholds;
  const eff = (rent == null || row.direct_revenue <= 0) ? -100 : rent;
  if (eff >= umbral_escalar)   return { key: "escalar",     label: "Escalar",      color: "green" };
  if (eff >= umbral_mantener)  return { key: "mantener",    label: "Mantener",     color: "blue" };
  if (eff >= 0)                return { key: "reducir_bid", label: "Reducir bid",  color: "orange" };
  if (halo != null && halo >= umbral_halo_proteger)
    return { key: "mantener", label: "Mantener", color: "blue", isAnchor: true };
  return { key: "pausar", label: "Pausar", color: "red" };
}

const CHIP_CLR = {
  green:  "bg-green-100 text-green-800 border border-green-200",
  blue:   "bg-blue-100  text-blue-800  border border-blue-200",
  orange: "bg-orange-100 text-orange-800 border border-orange-200",
  red:    "bg-red-100   text-red-800   border border-red-200",
  gray:   "bg-slate-100 text-slate-500  border border-slate-200",
};

function DecisionChip({ d }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${CHIP_CLR[d.color]}`}
      title={d.isAnchor ? "Producto ancla: rentabilidad negativa pero Halo Score significativo" : undefined}
      data-testid="meta-decision-chip">
      {d.isAnchor && <span>⚓</span>}
      {d.label}
    </span>
  );
}

const FILTER_CHIPS = [
  { key: "all",            label: "Todos",          dot: "bg-slate-400" },
  { key: "escalar",        label: "Escalar",        dot: "bg-green-500" },
  { key: "mantener",       label: "Mantener",       dot: "bg-blue-500" },
  { key: "reducir_bid",    label: "Reducir bid",    dot: "bg-orange-500" },
  { key: "pausar",         label: "Pausar",         dot: "bg-red-500" },
  { key: "sin_publicidad", label: "Sin publicidad", dot: "bg-slate-300" },
];

const SORT_FIELDS = new Set([
  "reference", "name", "impressions", "clicks", "ctr", "meta_spend",
  "direct_conversions", "direct_revenue", "direct_cost",
  "roas_meta", "rent_meta_pct", "cross_revenue", "halo_score", "rent_real_pct",
]);

const COLUMNS = [
  { key: "reference",           label: "Referencia",          align: "left",   sortable: true },
  { key: "name",                label: "Nombre",              align: "left",   sortable: true },
  { key: "impressions",         label: "Impr.",               align: "right",  sortable: true },
  { key: "clicks",              label: "Clics",               align: "right",  sortable: true },
  { key: "ctr",                 label: "CTR",                 align: "right",  sortable: true },
  { key: "meta_spend",          label: "Gasto Meta",          align: "right",  sortable: true },
  { key: "direct_conversions",  label: "Conv. Directas",      align: "right",  sortable: true },
  { key: "direct_revenue",      label: "Ing. Directos",       align: "right",  sortable: true },
  { key: "direct_cost",         label: "Coste Directo",       align: "right",  sortable: true },
  { key: "roas_meta",           label: "ROAS Meta",           align: "right",  sortable: true },
  { key: "rent_meta_pct",       label: "Rent. Meta %",        align: "right",  sortable: true, tooltip: "Solo incluye gasto de Meta Ads atribuido directamente a este producto." },
  { key: "cross_revenue",       label: "Ing. Cruzados",       align: "right",  sortable: true },
  { key: "halo_score",          label: "Halo Score",          align: "right",  sortable: true },
  { key: "_rent_real",          label: "Rent. Real %",        align: "right",  special: true },
  { key: "_decision",           label: "Decisión",            align: "center", special: true },
];

function getInitialDate(s) {
  const p = new URLSearchParams(s);
  if (p.get("preset") === "last30") return { from: subDays(new Date(), 29), to: new Date() };
  return { from: null, to: null };
}

export default function MetaAdsProductsPage() {
  const location = useLocation();
  const [dateRange, setDateRange]     = useState(() => getInitialDate(location.search));
  const [pendingDate, setPendingDate] = useState(() => getInitialDate(location.search));
  const [calOpen, setCalOpen]         = useState(false);
  const [search, setSearch]           = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows]               = useState([]);
  const [pagination, setPagination]   = useState({ page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
  const [sort, setSort]               = useState({ sortBy: "direct_revenue", sortOrder: "desc" });
  const [loading, setLoading]         = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [decisionCounts, setDecisionCounts] = useState({ escalar: 0, mantener: 0, reducir_bid: 0, pausar: 0, sin_publicidad: 0 });
  const [flags, setFlags] = useState({ no_product_data: false, no_utm_data: false, has_meta_insights: false, source: null });

  const hasRange = Boolean(dateRange.from && dateRange.to);

  const presets = useMemo(() => [
    { label: "Últimos 7 días",  from: subDays(new Date(), 6),                                    to: new Date() },
    { label: "Últimos 30 días", from: subDays(new Date(), 29),                                   to: new Date() },
    { label: "Este mes",        from: startOfMonth(new Date()),                                  to: new Date() },
    { label: "Mes pasado",      from: startOfMonth(subMonths(new Date(), 1)),                    to: endOfMonth(subMonths(new Date(), 1)) },
    { label: "Esta semana",     from: startOfWeek(new Date(), { weekStartsOn: 1 }),              to: new Date() },
    { label: "Semana pasada",   from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
  ], []);

  const enrichedRows = useMemo(() => rows.map((row) => ({
    ...row,
    _decision: calcDecision(row),
  })), [rows]);

  useEffect(() => {
    const t = setTimeout(() => { setPagination((p) => ({ ...p, page: 1 })); setDebouncedSearch(search); }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPagination((p) => ({ ...p, page: 1 })); }, [activeFilter]);

  const fetchData = useCallback(async (page = 1) => {
    if (!dateRange.from || !dateRange.to) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const orgId = getActiveOrgId();
      const res = await axios.get(`${API}/analytics/meta-ads-products`, {
        params: {
          start_date:      format(dateRange.from, "yyyy-MM-dd"),
          end_date:        format(dateRange.to,   "yyyy-MM-dd"),
          search:          debouncedSearch.trim() || undefined,
          sort_by:         SORT_FIELDS.has(sort.sortBy) ? sort.sortBy : "direct_revenue",
          sort_order:      sort.sortOrder,
          decision_filter: activeFilter !== "all" ? activeFilter : undefined,
          page,
          page_size: 50,
        },
        withCredentials: true,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(orgId ? { "X-Org-Id": orgId } : {}),
        },
      });
      setFlags({
        no_product_data: !!res.data.no_product_data,
        no_utm_data:     !!res.data.no_utm_data,
        has_meta_insights: !!res.data.has_meta_insights,
        source:          res.data.source || null,
      });
      setRows(res.data.rows || []);
      if (res.data.decision_counts) setDecisionCounts(res.data.decision_counts);
      setPagination(res.data.pagination || { page: 1, page_size: 50, total_rows: 0, total_pages: 1 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando datos de Meta Ads");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange, debouncedSearch, sort, activeFilter]);

  useEffect(() => { if (hasRange) fetchData(1); }, [hasRange, dateRange, debouncedSearch, activeFilter]);
  useEffect(() => { if (hasRange) fetchData(pagination.page); }, [sort]);

  const onSort = (key) => {
    setSort((s) => s.sortBy === key
      ? { ...s, sortOrder: s.sortOrder === "asc" ? "desc" : "asc" }
      : { sortBy: key, sortOrder: "desc" });
  };
  const SortIcon = ({ k }) => {
    if (sort.sortBy !== k) return <ArrowUpDown className="w-3 h-3" />;
    return sort.sortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  const canPrev = pagination.page > 1;
  const canNext = pagination.page < pagination.total_pages;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="meta-ads-products-page">

        {/* Header */}
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, #1877F2, #42b883)" }}>
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">Meta Ads — Productos</h1>
              <p className="text-sm text-slate-500">Atribución por SKU desde Meta Insights API + GA4 / Pixel</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" asChild><Link to="/analytics">GA4</Link></Button>
            <Button variant="outline" asChild><Link to="/analytics/products">Productos</Link></Button>
            <Button variant="outline" asChild><Link to="/analytics/google-ads-products">GAds — Productos</Link></Button>
            <Button
              className="text-white"
              style={{ background: "linear-gradient(135deg, #1877F2, #42b883)" }}
              asChild
            >
              <Link to="/analytics/meta-ads-products">Meta — Productos</Link>
            </Button>
          </div>
        </div>

        {/* Date picker + search */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <Popover open={calOpen} onOpenChange={(o) => { setCalOpen(o); if (o) setPendingDate(dateRange); }}>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="meta-products-date-picker">
                  <Calendar className="w-4 h-4 mr-2" />
                  {hasRange
                    ? `${format(dateRange.from, "d MMM", { locale: es })} – ${format(dateRange.to, "d MMM yyyy", { locale: es })}`
                    : "Selecciona rango de fechas"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="flex">
                  <div className="border-r border-slate-200 p-2 space-y-1 min-w-[170px]">
                    <p className="text-xs font-semibold text-slate-500 uppercase px-2 py-1">Accesos rápidos</p>
                    {presets.map((p) => (
                      <button key={p.label}
                        onClick={() => { const r = { from: p.from, to: p.to }; setDateRange(r); setPendingDate(r); setCalOpen(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 flex flex-col">
                    <CalendarComponent mode="range" selected={pendingDate}
                      onSelect={(r) => setPendingDate({ from: r?.from || null, to: r?.to || null })}
                      numberOfMonths={2} locale={es} />
                    <div className="border-t pt-2 flex justify-end">
                      <Button size="sm" style={{ background: "linear-gradient(135deg, #1877F2, #42b883)" }}
                        className="text-white"
                        disabled={!pendingDate.from}
                        onClick={() => { const r = { from: pendingDate.from, to: pendingDate.to || pendingDate.from }; setDateRange(r); setCalOpen(false); }}>
                        Aplicar
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            <div className="relative w-full lg:w-96">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search}
                onChange={(e) => { setPagination((p) => ({ ...p, page: 1 })); setSearch(e.target.value); }}
                placeholder="Buscar referencia o nombre..."
                className="pl-9" data-testid="meta-products-search" />
            </div>
          </CardContent>
        </Card>

        {/* Empty state / banners */}
        {!hasRange ? (
          <Card className="bg-amber-50 border border-amber-200">
            <CardContent className="p-6">
              <p className="text-amber-900 font-semibold">Selecciona un rango de fechas para ver los datos</p>
            </CardContent>
          </Card>
        ) : flags.no_product_data && flags.no_utm_data ? (
          <Card className="bg-blue-50 border border-blue-200" data-testid="meta-no-product-banner">
            <CardContent className="p-6 flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-blue-900">No se encontraron datos por producto de Meta Ads</p>
                <p className="text-sm text-blue-800 mt-1">
                  Esta tabla requiere <strong>campañas de catálogo activas</strong> (Advantage+ Catalog o DPA) en Meta para obtener datos de gasto por SKU,
                  o bien UTMs de Meta en tus pedidos para atribución por pixel.
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  Si tuviste campañas DPA activas anteriormente, prueba con un rango de fechas más amplio que incluya ese periodo.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CardTitle className="text-lg font-bold text-slate-900">Atribución por Producto — Meta Ads</CardTitle>

                    {flags.has_meta_insights && (
                      <span data-testid="meta-source-badge-insights"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Datos via Meta API
                      </span>
                    )}
                    {flags.source === "ga4" && (
                      <span data-testid="meta-source-badge-ga4"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                        Atribución via GA4
                      </span>
                    )}
                    {flags.source === "pixel" && (
                      <span data-testid="meta-source-badge-pixel"
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Atribución via Pixel
                      </span>
                    )}
                    {flags.has_meta_insights && (flags.source === "ga4" || flags.source === "pixel") && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
                        Meta API + GA4
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {loading ? "Cargando..." : `${pagination.total_rows} productos`}
                  </p>

                  {/* Banners */}
                  {flags.no_product_data && !flags.no_utm_data && (
                    <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Las columnas de Gasto, Impresiones y Clics no están disponibles porque no se detectaron campañas de catálogo (DPA / Advantage+) activas en este periodo.
                    </div>
                  )}
                  {!flags.no_product_data && flags.no_utm_data && (
                    <div className="mt-2 rounded bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-800 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      Conecta GA4 o activa el pixel para ver conversiones reales por producto atribuidas a Meta.
                    </div>
                  )}
                </div>
              </div>

              {/* Filter chips */}
              <div className="flex flex-wrap gap-2 mt-3" data-testid="meta-decision-filter-chips">
                {FILTER_CHIPS.map((chip) => {
                  const active = activeFilter === chip.key;
                  const cnt = chip.key === "all" ? enrichedRows.length : (decisionCounts[chip.key] ?? 0);
                  return (
                    <button key={chip.key} type="button" onClick={() => setActiveFilter(chip.key)}
                      data-testid={`meta-filter-chip-${chip.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all
                        ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
                      <span className={`w-2 h-2 rounded-full ${chip.dot}`} />
                      {chip.label}
                      <span className={`ml-0.5 ${active ? "text-slate-300" : "text-slate-400"}`}>{cnt}</span>
                    </button>
                  );
                })}
              </div>
            </CardHeader>

            <CardContent>
              <div className="overflow-x-auto" data-testid="meta-products-table-wrapper">
                <Table data-testid="meta-products-table">
                  <TableHeader>
                    <TableRow>
                      {COLUMNS.map((col) => (
                        <TableHead key={col.key}
                          className={`${col.align === "left" ? "text-left" : col.align === "center" ? "text-center" : "text-right"}
                            ${col.key === "_rent_real" ? "bg-slate-50 border-l border-slate-200" : ""}
                            ${col.key === "_decision"  ? "bg-slate-50 text-center" : ""}`}>
                          {col.special ? (
                            <span className="font-bold text-slate-800 text-xs">{col.label}</span>
                          ) : col.tooltip ? (
                            <span className="inline-flex items-center gap-1 text-slate-600 justify-end w-full text-xs font-medium" title={col.tooltip}>
                              {col.label} <Info className="w-3 h-3 text-slate-400" />
                            </span>
                          ) : (
                            <button type="button" onClick={() => col.sortable && onSort(col.key)}
                              className={`inline-flex items-center gap-1 text-slate-600 hover:text-slate-900
                                ${col.align === "left" ? "" : "justify-end w-full"}`}>
                              {col.label}
                              {col.sortable && <SortIcon k={col.key} />}
                            </button>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!loading && enrichedRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length} className="text-center text-slate-500 py-10">
                          {activeFilter !== "all" ? "No hay productos con esta clasificación." : "Sin datos para este rango."}
                        </TableCell>
                      </TableRow>
                    )}
                    {enrichedRows.map((row, i) => {
                      const d = row._decision;
                      return (
                        <TableRow key={row.product_uid || i} data-testid={`meta-row-${i}`}>
                          <TableCell className="font-semibold text-slate-900">{row.reference || "—"}</TableCell>
                          <TableCell className="text-slate-700 max-w-[180px] truncate">{row.name || "—"}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700">{fmtInt(row.impressions)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700">{fmtInt(row.clicks)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700">{row.ctr != null ? fmtPct(row.ctr) : "—"}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700">{fmtEur(row.meta_spend)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-700">
                            {row.direct_conversions > 0 ? fmtInt(row.direct_conversions) : "—"}
                          </TableCell>
                          <TableCell className={`text-right font-mono font-bold ${row.direct_revenue > 0 ? "text-blue-700" : "text-slate-400"}`}>
                            {row.direct_revenue > 0 ? fmtEur(row.direct_revenue) : "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-700">
                            {row.direct_cost > 0 ? fmtEur(row.direct_cost) : "—"}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${row.roas_meta != null && row.roas_meta >= 2 ? "text-green-700 font-semibold" : row.roas_meta != null && row.roas_meta < 1 ? "text-red-600" : "text-slate-700"}`}>
                            {fmtX(row.roas_meta)}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${row.rent_meta_pct != null && row.rent_meta_pct < 0 ? "text-red-600 font-semibold" : row.rent_meta_pct != null && row.rent_meta_pct >= 20 ? "text-green-700 font-semibold" : "text-slate-700"}`}>
                            {fmtPct(row.rent_meta_pct)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-500 text-sm">
                            {row.cross_revenue > 0 ? fmtEur(row.cross_revenue) : "—"}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${row.halo_score != null && row.halo_score > 1 ? "text-blue-600 font-semibold" : "text-slate-500"}`}>
                            {fmtX(row.halo_score)}
                          </TableCell>
                          {/* Rent. Real % */}
                          <TableCell className="text-right bg-slate-50 border-l border-slate-100" data-testid={`meta-row-${i}-rent-real`}>
                            {row.rent_real_pct != null ? (
                              <span className={`font-mono font-semibold ${row.rent_real_pct >= 20 ? "text-green-700" : row.rent_real_pct >= 0 ? "text-amber-600" : "text-red-600"}`}>
                                {fmtPct(row.rent_real_pct)}
                              </span>
                            ) : <span className="text-slate-400 font-mono">—</span>}
                          </TableCell>
                          {/* Decisión */}
                          <TableCell className="text-center bg-slate-50" data-testid={`meta-row-${i}-decision`}>
                            <DecisionChip d={d} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {pagination.total_rows > 50 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-500">Página {pagination.page} de {pagination.total_pages} · {pagination.total_rows} productos</p>
                  <div className="flex gap-2">
                    <Button variant="outline" disabled={!canPrev || loading}
                      onClick={() => { const p = pagination.page - 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>
                      Anterior
                    </Button>
                    <Button variant="outline" disabled={!canNext || loading}
                      onClick={() => { const p = pagination.page + 1; setPagination((pr) => ({ ...pr, page: p })); fetchData(p); }}>
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
